import { Link, useLocation } from 'react-router-dom';
import { PencilSimple, User } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import type { Topic } from '@researcher/shared';

interface CommunitySidebarProps {
  topics: Topic[];
  onClose?: () => void;
}

export default function CommunitySidebar({ topics, onClose }: CommunitySidebarProps) {
  const { currentUser } = useAuth();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const linkClass = (active: boolean) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
      active
        ? 'bg-pink-50 dark:bg-pink-950 text-pink-700 dark:text-pink-300 font-medium'
        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-100'
    }`;

  return (
    <aside className="w-full">
      {/* New Post button — always reserve space to prevent layout shift */}
      {currentUser ? (
        <Link
          to="/community/new"
          onClick={onClose}
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-medium text-sm shadow-sm mb-5"
        >
          <PencilSimple size={18} weight="bold" />
          New Post
        </Link>
      ) : (
        <div className="h-[42px] mb-5" />
      )}

      {/* My Posts — always reserve space */}
      {currentUser ? (
        <nav className="mb-6">
          <Link to="/community/my-posts" onClick={onClose} className={linkClass(isActive('/community/my-posts'))}>
            <User size={18} />
            My Posts
          </Link>
        </nav>
      ) : (
        <div className="h-[36px] mb-6" />
      )}

      {/* Topics */}
      <div className="mb-6">
        <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 mb-2">Topics</h3>
        <nav className="space-y-0.5">
          <Link
            to="/community"
            onClick={onClose}
            className={linkClass(isActive('/community'))}
          >
            All Posts
          </Link>
          {topics.length === 0 ? (
            // Skeleton placeholders while topics load
            <>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="flex items-center gap-2.5 px-3 py-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" style={{ width: `${50 + (i * 11) % 40}%` }} />
                </div>
              ))}
            </>
          ) : (
            topics.map((topic) => (
              <Link
                key={topic.slug}
                to={`/community/${topic.slug}`}
                onClick={onClose}
                className={linkClass(isActive(`/community/${topic.slug}`))}
              >
                {topic.name}
              </Link>
            ))
          )}
        </nav>
      </div>

      {/* Community Guidelines */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Community Guidelines</h3>
        <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-1.5">
          <li>Be respectful and supportive</li>
          <li>No medical advice — share experiences only</li>
          <li>Protect your privacy and others&apos;</li>
          <li>Report harmful content</li>
        </ul>
      </div>
    </aside>
  );
}
